/**
 * OnboardingPathChoice — Iter. 411.hh
 * ─────────────────────────────────────
 * Branching gate shown at the top of `/onboarding`, before the classic
 * multi-step wizard. Presents two clear paths so gym-experienced users
 * are no longer forced through the 7-day tutorial.
 *
 * Props:
 *   language        current UI language (fr/en/es)
 *   t               translation function
 *   onAccompanied   callback when user picks Option A (guided)
 *   onBypass        callback when user picks Option B (fast-path)
 *
 * Design: GLOW aesthetic, matches the rest of YUMIX. Two big vertical
 * cards, one orange (recommended) + one violet (expert). Each card owns
 * a distinct emoji + tagline so the visual weight is symmetric and the
 * user can pick on a glance.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Zap, Rocket, Shield } from 'lucide-react';

export default function OnboardingPathChoice({ language, t, onAccompanied, onBypass }) {
  const fr = language === 'fr';
  const es = language === 'es';

  const title = fr
    ? 'Comment veux-tu commencer ?'
    : es
      ? '¿Cómo quieres empezar?'
      : 'How do you want to start?';

  const subtitle = fr
    ? 'Deux chemins — même destination. Choisis celui qui te ressemble.'
    : es
      ? 'Dos caminos — el mismo destino. Elige el que se te parece.'
      : 'Two paths — same destination. Pick the one that fits you.';

  const optionA = {
    tag: fr ? 'Recommandé' : 'Recommended',
    title: fr
      ? 'Je veux être accompagné'
      : es
        ? 'Quiero ser acompañado'
        : 'I want to be guided',
    subtitle: fr
      ? 'Le Défi 7 jours te tient la main : missions, badges, coaching pas à pas.'
      : es
        ? 'El Reto de 7 días te acompaña: misiones, insignias, coaching paso a paso.'
        : 'The 7-day Challenge holds your hand: missions, badges, step-by-step coaching.',
    cta: fr ? 'Lancer le parcours' : es ? 'Iniciar el trayecto' : 'Start the journey',
  };

  const optionB = {
    tag: fr ? 'Expert' : es ? 'Experto' : 'Expert',
    title: fr
      ? 'Je connais le gym'
      : es
        ? 'Ya conozco el gym'
        : 'I know the gym',
    subtitle: fr
      ? 'Aller à l\'essentiel : profil rapide, puis test direct du Coach YUMIX.'
      : es
        ? 'Ir a lo esencial: perfil rápido, luego prueba directa del Coach YUMIX.'
        : 'Cut to the chase: quick profile, then test Coach YUMIX right now.',
    cta: fr ? 'Aller au Coach' : es ? 'Ir al Coach' : 'Go to Coach',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full max-w-3xl mx-auto py-4"
      data-testid="onboarding-path-choice"
    >
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 mb-3">
          <Sparkles
            className="w-4 h-4"
            style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 5px rgba(251,146,60,0.85))' }}
          />
          <span
            className="text-[11px] font-black uppercase tracking-widest"
            style={{ color: '#fed7aa', textShadow: '0 0 5px rgba(251,146,60,0.55)' }}
          >
            {fr ? 'Choix de Parcours' : es ? 'Elección de Camino' : 'Path Choice'}
          </span>
          <Sparkles
            className="w-4 h-4"
            style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 5px rgba(251,146,60,0.85))' }}
          />
        </div>
        <h1
          className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-white leading-tight"
          style={{ textShadow: '0 0 12px rgba(251,146,60,0.5)' }}
          data-testid="path-choice-title"
        >
          {title}
        </h1>
        <p className="mt-2 text-sm text-white/70 max-w-xl mx-auto">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Option A — Accompanied (orange, recommended) */}
        <motion.button
          type="button"
          whileHover={{ y: -4 }}
          whileTap={{ scale: 0.98 }}
          onClick={onAccompanied}
          data-testid="path-choice-accompanied-btn"
          className="relative text-left rounded-2xl p-6 flex flex-col gap-4 group focus:outline-none"
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.22) 100%)',
            border: '1.5px solid rgba(251,146,60,0.55)',
            boxShadow: '0 0 22px rgba(251,146,60,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <span
            className="absolute -top-2 right-4 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full"
            style={{
              background: 'linear-gradient(140deg, #fb923c 0%, #f97316 100%)',
              color: '#fff',
              boxShadow: '0 0 10px rgba(251,146,60,0.7)',
              textShadow: '0 0 5px rgba(0,0,0,0.35)',
            }}
          >
            {optionA.tag}
          </span>

          <div className="flex items-center gap-3">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background: 'linear-gradient(140deg, rgba(251,146,60,0.5), rgba(251,146,60,0.2))',
                boxShadow: '0 0 16px rgba(251,146,60,0.65), inset 0 0 0 1.5px rgba(251,146,60,0.6)',
              }}
            >
              <Shield
                className="w-7 h-7"
                style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 8px rgba(251,146,60,0.9))' }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className="font-heading text-lg font-black uppercase tracking-tight leading-tight"
                style={{ color: '#fed7aa', textShadow: '0 0 6px rgba(251,146,60,0.65)' }}
              >
                {optionA.title}
              </h2>
              <p className="text-xs text-white/60 mt-0.5">
                {fr ? '7 jours · guidé' : es ? '7 días · guiado' : '7 days · guided'}
              </p>
            </div>
          </div>

          <p className="text-sm text-white/80 leading-relaxed">{optionA.subtitle}</p>

          <div
            className="mt-auto inline-flex items-center gap-2 rounded-full self-start px-4 py-2 transition-transform group-hover:translate-x-1"
            style={{
              background: 'linear-gradient(140deg, #fb923c 0%, #f97316 100%)',
              color: '#fff',
              boxShadow: '0 0 12px rgba(251,146,60,0.55), inset 0 1px 0 rgba(255,255,255,0.2)',
              textShadow: '0 0 4px rgba(0,0,0,0.35)',
            }}
          >
            <Rocket className="w-4 h-4" />
            <span className="text-[11px] font-black uppercase tracking-widest">{optionA.cta}</span>
          </div>
        </motion.button>

        {/* Option B — Bypass (violet, expert) */}
        <motion.button
          type="button"
          whileHover={{ y: -4 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBypass}
          data-testid="path-choice-bypass-btn"
          className="relative text-left rounded-2xl p-6 flex flex-col gap-4 group focus:outline-none"
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(168,85,247,0.22) 100%)',
            border: '1.5px solid rgba(168,85,247,0.55)',
            boxShadow: '0 0 22px rgba(168,85,247,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <span
            className="absolute -top-2 right-4 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full"
            style={{
              background: 'linear-gradient(140deg, #a855f7 0%, #7c3aed 100%)',
              color: '#fff',
              boxShadow: '0 0 10px rgba(168,85,247,0.7)',
              textShadow: '0 0 5px rgba(0,0,0,0.35)',
            }}
          >
            {optionB.tag}
          </span>

          <div className="flex items-center gap-3">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background: 'linear-gradient(140deg, rgba(168,85,247,0.5), rgba(168,85,247,0.2))',
                boxShadow: '0 0 16px rgba(168,85,247,0.65), inset 0 0 0 1.5px rgba(168,85,247,0.6)',
              }}
            >
              <Zap
                className="w-7 h-7"
                style={{ color: '#ddd6fe', filter: 'drop-shadow(0 0 8px rgba(168,85,247,0.9))' }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className="font-heading text-lg font-black uppercase tracking-tight leading-tight"
                style={{ color: '#ddd6fe', textShadow: '0 0 6px rgba(168,85,247,0.65)' }}
              >
                {optionB.title}
              </h2>
              <p className="text-xs text-white/60 mt-0.5">
                {fr ? '~1 min · essentiel' : es ? '~1 min · esencial' : '~1 min · essentials'}
              </p>
            </div>
          </div>

          <p className="text-sm text-white/80 leading-relaxed">{optionB.subtitle}</p>

          <div
            className="mt-auto inline-flex items-center gap-2 rounded-full self-start px-4 py-2 transition-transform group-hover:translate-x-1"
            style={{
              background: 'linear-gradient(140deg, #a855f7 0%, #7c3aed 100%)',
              color: '#fff',
              boxShadow: '0 0 12px rgba(168,85,247,0.55), inset 0 1px 0 rgba(255,255,255,0.2)',
              textShadow: '0 0 4px rgba(0,0,0,0.35)',
            }}
          >
            <Zap className="w-4 h-4" />
            <span className="text-[11px] font-black uppercase tracking-widest">{optionB.cta}</span>
          </div>
        </motion.button>
      </div>

      <p className="text-center text-[10px] text-white/40 mt-6 italic px-4">
        {fr
          ? 'Tu pourras toujours activer les missions guidées plus tard depuis les Réglages.'
          : es
            ? 'Podrás activar las misiones guiadas más tarde desde Ajustes.'
            : 'You can always turn on guided missions later from Settings.'}
      </p>
    </motion.div>
  );
}
